import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import Paper from '@mui/material/Paper';

import { StyledDoughnutChartCard } from 'components/Cards/PassportChartCard/DoughnutChartCard/style';
import { addComma } from 'utils';
import { formatDateToStringForEntry } from 'utils/formatDateToString';

ChartJS.register(ArcElement, Tooltip, Legend);

export const DoughnutChartCard = ({
  entryData = [],
  title = 'title',
  isToday,
  isdashboard = false,
  onClick,
  ...rest
}) => {
  let legendRef = useRef(null);
  const chartRef = useRef(null);

  const chartColors = [
    '#1A3670',
    '#306CD2',
    '#498BEB',
    '#5FA6F8',
    '#9265C7',
    '#BC9FE5',
    '#E9DFF5',
    '#E3E3E3'
  ];

  /**
   * 차트에서 legend 선택 시 실제 데이터로 컨트롤해서 차트에 반영하기때문에
   * label 보여줄때 totalEmployees 초기데이터로 대체해서 보여지도록 ( legend 와 도넛차트 안에 텍스트를 labelShow로 대체 )
   */
  const [labelShow, setLabelShow] = useState([]);
  const [isNoEntry, setIsNoEntry] = useState(true);

  function generateRandomColors(count) {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const baseColor = chartColors[i % chartColors.length];
      colors.push(baseColor);
    }
    return colors;
  }
  useEffect(() => {
    setLabelShow(entryData?.map((v) => v.totalEmployees));
  }, [entryData]);

  useEffect(() => {
    const isEntryTotalZero =
      labelShow.reduce((acc, cur) => {
        return acc + cur;
      }, 0) === 0
        ? true
        : false;

    setIsNoEntry(isEntryTotalZero);
  }, [labelShow]);

  const data = {
    labels: entryData?.map((v) => v.companyName),
    datasets: [
      {
        hoverOffset: 10,
        label: `Total Workers`,
        data: isNoEntry ? [1] : entryData?.map((v) => v.totalEmployees),
        backgroundColor: isNoEntry ? ['#E3E3E3'] : generateRandomColors(entryData.length),
        borderWidth: 0
      },
      {
        label: ` Workers`,
        data: entryData?.map((v) => v.todayPresentEmployees),
        borderWidth: 0, // 선 없음
        hidden: true
      }
    ]
  };

  // Define a custom plugin to draw text
  const drawTextPlugin = {
    beforeDraw: function (chart) {
      const { ctx, data, config } = chart;
      ctx.save();
      const xCoor = chart.getDatasetMeta(0).data[0].x;
      const yCoor = chart.getDatasetMeta(0).data[0].y;

      ctx.fillStyle = '#2F2F35'; // 텍스트 색상
      ctx.textAlign = 'center'; // 텍스트 정렬

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const current = data.datasets[1].data.reduce((acc, cur) => {
        return acc + cur;
      }, 0);
      const total = config.options.labelShow.reduce((acc, cur) => {
        return acc + cur;
      }, 0);

      let text = '';

      if (config.options.isToday) {
        text = `${addComma(current)} / ${addComma(total)}\n Workers`;
        // text = `100 / 1000\n Workers`;
      } else {
        text = `${addComma(total)}\n Workers`;
      }
      ctx.font = `600 ${text.length < 20 ? '15px' : '11px'} Poppins, sans-serif`; // 폰트 스타일 설정

      // 텍스트를 개별적으로 그리기
      const lines = text.split('\n');

      lines.forEach((line, i) => {
        ctx.fillText(line, xCoor, yCoor - 5 + i * 15); // text size만큼 간격을 주어 line break처럼 처리
      });
    }
  };

  function legendClickHandler(chart, datasetIndex, realValue) {
    if (chart.data.datasets[0].data[datasetIndex] === 0) {
      chart.data.datasets[0].data[datasetIndex] = realValue;
    } else {
      chart.data.datasets[0].data[datasetIndex] = 0;
    }

    chart.update();
  }

  const htmlLegendPlugin = {
    id: 'htmlLegend',
    ulElement: null,
    afterUpdate(chart) {
      this.clearLegend(legendRef.current);

      if (this.ulElement) {
        // 이전 레전드를 삭제합니다.
        this.ulElement.remove();
      }

      const ul = document.createElement('ul');
      this.ulElement = ul; // 현재 ul 요소를 추적합니다.

      if (!chart.config.options.isNoEntry) {
        ul.style.cursor = 'pointer';
        ul.scrollTop = 0;
        const items = chart.options.plugins.legend.labels.generateLabels(chart);

        items.forEach((item, index) => {
          const li = document.createElement('li');

          li.addEventListener('click', () => {
            onClick && onClick(entryData[index]);
          });

          const colorBox = document.createElement('div'); // 박스 색상을 표시할 요소

          // 박스 색상 스타일 설정
          colorBox.style.backgroundColor = item.fillStyle; // 항목의 색상 사용
          colorBox.style.width = '16px'; // 원하는 크기로 조절 가능
          colorBox.style.height = '16px'; // 원하는 크기로 조절 가능
          colorBox.style.display = 'inline-block';
          colorBox.style.borderRadius = '50%';

          li.style.display = 'flex';
          li.style.alignItems = 'center';
          li.style.justifyContent = 'space-between';
          li.style.gap = '14px';

          li.appendChild(colorBox);

          let textArr = item.text.split(',');

          const spanLabel = document.createElement('span');
          const spanData = document.createElement('span');

          if (textArr.length === 3) {
            spanLabel.appendChild(document.createTextNode(textArr[0]));
            spanData.appendChild(document.createTextNode(`${textArr[2]}/${textArr[1]}`));
          } else if (textArr.length === 2) {
            spanLabel.appendChild(document.createTextNode(textArr[0]));
            spanData.appendChild(document.createTextNode(textArr[1]));
          }

          spanLabel.style.width = '120px';
          spanLabel.style.textAlign = 'left';
          spanLabel.style.fontSize = '12px';
          spanData.style.fontSize = '12px';
          spanData.style.width = '30px';
          spanData.style.textAlign = 'center';

          li.appendChild(spanLabel);
          li.appendChild(spanData);

          ul.appendChild(li);
        });

        const jsLegend = legendRef.current;
        jsLegend.style.display = '';
        jsLegend.style.alignItems = '';
        jsLegend.appendChild(ul);

        this.ulCreated = true;
      } else {
        const jsLegend = legendRef.current;

        jsLegend.innerHTML = '';
        const li = document.createElement('li');
        const span = document.createElement('span');
        // span.appendChild(
        //   document.createTextNode(`There is no list of companies available to view.`)
        // );
        li.appendChild(span);
        ul.appendChild(li);

        jsLegend.style.display = 'flex';
        jsLegend.style.alignItems = 'center';
        jsLegend.appendChild(ul);
      }
    },
    clearLegend(container) {
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    }
  };

  const options = {
    cutout: '60%',
    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        top: 6,
        left: 0,
        bottom: 6,
        right: 200
      }
    },
    plugins: {
      legend: {
        maxHeight: 200,
        position: 'right',
        align: 'center',
        display: false,
        labels: {
          borderWidth: 0,
          useBorderRadius: true,
          borderRadius: 1,
          usePointStyle: true,
          textAlign: 'right',
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;

            const data1 = datasets[0].data;
            const data2 = datasets[1].data;

            const combinedDatasets = data1.map((item, index) => {
              return [item, data2[index]];
            });

            return datasets[0].data.map((data, i) => ({
              text: isToday
                ? `${chart.data.labels[i]},${labelShow[i]},${combinedDatasets[i][1]}`
                : `${chart.data.labels[i]},${labelShow[i]}`,
              fillStyle: datasets[0].backgroundColor[i],
              index: i,
              lineWidth: 0 // legend label borderWidth,
            }));
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: !isNoEntry,
        mode: 'index',
        backgroundColor: 'white',
        titleColor: 'black',
        bodyColor: 'black',
        position: 'nearest',
        padding: 12,
        caretSize: 6,
        callbacks: {
          label: function (context) {
            const value = context.formattedValue || '';
            // HTML 요소를 사용하여 줄 바꿈 추가
            return `
            ${value}\r
            Workers`;
          }
        }
      }
    },
    onClick: (e, activeEls) => {
      if (activeEls.length > 0 && activeEls[0]) {
        let datasetIndex = activeEls[0].datasetIndex;
        let dataIndex = activeEls[0].index;

        onClick && onClick(entryData[dataIndex]);
      } else {
        console.log('No active elements');
      }
    }
  };

  return (
    <StyledDoughnutChartCard isdashboard={isdashboard ? 'dashboard' : null}>
      {isdashboard === true ? (
        <div className="status-chart-wrapper">
          <div className="chart-card-content">
            <Doughnut
              data={data}
              ref={chartRef}
              options={{
                ...options,
                isToday,
                labelShow,
                isNoEntry
              }}
              redraw
              updateMode="resize"
              /** 
           * 1. Props를 drawTextPlugin에서 사용하기 위해 isToday를 전달하고 chart.config.options 에서 꺼내씁니다.
             2. labelShow를 사용할 수 없어 Props로 전달하여 사용
          */
              plugins={[drawTextPlugin, htmlLegendPlugin]}
            />
            <div ref={legendRef} className="chart-card-content-legend"></div>
          </div>
        </div>
      ) : (
        <Paper className={`chart-card`}>
          <div className="chart-card-header">
            <div className="chart-card-header-title">
              <p className="title">{title}</p>
              {/* <img className="icon" src={titleIcon} alt="icon" /> */}
            </div>
            {/* <div className="chart-card-header-content">{childContent}</div> */}
          </div>
          <div className="chart-card-content">
            <Doughnut
              data={data}
              ref={chartRef}
              options={{
                ...options,
                isToday,
                labelShow,
                isNoEntry
              }}
              redraw
              updateMode="resize"
              /** 
             * 1. Props를 drawTextPlugin에서 사용하기 위해 isToday를 전달하고 chart.config.options 에서 꺼내씁니다.
               2. labelShow를 사용할 수 없어 Props로 전달하여 사용
            */
              plugins={[drawTextPlugin, htmlLegendPlugin]}
            />
            <div ref={legendRef} className="chart-card-content-legend"></div>
          </div>
          <div className="chart-card-footer" style={{ visibility: 'hidden' }}>
            {formatDateToStringForEntry()}
          </div>
        </Paper>
      )}
    </StyledDoughnutChartCard>
  );
};
